<template>
    <div
        v-if="show"
        class="ppv2-fixed ppv2-inset-0 ppv2-z-[1000] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-p-4"
        @keydown.esc="$emit('close')"
    >
        <!-- Overlay -->
        <div class="ppv2-absolute ppv2-inset-0 ppv2-bg-black ppv2-bg-opacity-50" @click="$emit('close')"></div>

        <!-- Modale -->
        <div
            ref="dialog"
            role="dialog"
            aria-modal="true"
            :aria-describedby="hasMissing ? 'exp-dates-alert' : null"
            tabindex="-1"
            class="ppv2-relative ppv2-bg-white ppv2-rounded-2xl ppv2-shadow-2xl ppv2-w-full ppv2-max-w-[520px] ppv2-p-6 ppv2-outline-none"
        >
            <!-- Bouton fermer -->
            <button
                type="button"
                class="ppv2-absolute ppv2-top-4 ppv2-right-4 ppv2-w-8 ppv2-h-8 ppv2-flex ppv2-items-center ppv2-justify-center ppv2-rounded-full ppv2-bg-black ppv2-bg-opacity-5 hover:ppv2-bg-opacity-10 ppv2-transition-colors"
                :aria-label="$t('common.close')"
                @click="$emit('close')"
            >
                <X :size="16" />
            </button>

            <!-- Titre + sous-titre -->
            <h2 class="poppinsSemiBold ppv2-text-[20px] ppv2-text-[#1d1d1b] ppv2-pr-8">
                {{ $t('quotes.experience_dates.title') }}
            </h2>
            <p class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-50 poppinsRegular ppv2-mt-1">
                {{ subtitle }}
            </p>

            <!-- Section -->
            <p class="poppinsSemiBold ppv2-text-xs ppv2-text-black ppv2-text-opacity-70 ppv2-mt-5 ppv2-mb-2">
                {{ $t('quotes.experience_dates.section') }}
            </p>

            <!-- Liste des expériences -->
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-2 ppv2-max-h-[280px] ppv2-overflow-y-auto">
                <div
                    v-for="exp in experiences"
                    :key="exp.id"
                    class="ppv2-rounded-xl ppv2-p-3 ppv2-border ppv2-border-solid"
                    :class="
                        !exp.date
                            ? 'ppv2-border-red-300 ppv2-bg-red-50 ppv2-bg-opacity-60'
                            : exp.is_past
                              ? 'ppv2-border-amber-300 ppv2-bg-amber-50 ppv2-bg-opacity-60'
                              : 'ppv2-border-black ppv2-border-opacity-10'
                    "
                >
                    <div class="poppinsSemiBold ppv2-text-sm ppv2-text-[#1d1d1b]">{{ exp.name }}</div>
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mt-1 ppv2-text-sm">
                        <span class="ppv2-text-black ppv2-text-opacity-60 poppinsRegular"
                            >{{ $t('quotes.experience_dates.date_label') }}
                        </span>
                        <template v-if="exp.date">
                            <span class="poppinsMedium ppv2-text-[#1d1d1b]">{{ exp.date }}</span>
                            <!-- Date dépassée : warning NON bloquant (la facturation rétroactive reste
                                 permise). On propose quand même de redater via le sélecteur de créneau. -->
                            <template v-if="exp.is_past">
                                <span
                                    class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full ppv2-text-[10px] poppinsMedium ppv2-bg-amber-100 ppv2-text-amber-700"
                                >
                                    {{ $t('quotes.experience_dates.date_past') }}
                                </span>
                                <button
                                    type="button"
                                    class="poppinsMedium ppv2-underline ppv2-text-[#7d2dbd]"
                                    @click="openSlotEditor(exp)"
                                >
                                    {{ $t('quotes.experience_dates.modify_date') }}
                                </button>
                            </template>
                        </template>
                        <template v-else>
                            <button
                                type="button"
                                class="poppinsMedium ppv2-underline ppv2-text-[#7d2dbd]"
                                @click="openSlotEditor(exp)"
                            >
                                {{ $t('quotes.experience_dates.choose_date') }}
                            </button>
                            <span
                                class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full ppv2-text-[10px] poppinsMedium ppv2-bg-red-100 ppv2-text-red-600"
                            >
                                {{ $t('quotes.experience_dates.required') }}
                            </span>
                        </template>
                    </div>
                    <!-- Capacité (INFORMATIVE, jamais bloquante) : sur une ligne datée dont le créneau
                         a une capacité connue, on montre « Capacité OK · dispo/max places » (vert) ou
                         un warning ambre si les participants dépassent les places restantes. Capacité
                         inconnue (non datée, ou non calculable) → on retombe sur le nb de participants. -->
                    <div class="ppv2-mt-1 ppv2-text-xs">
                        <span
                            v-if="exp.date && hasCapacity(exp)"
                            class="ppv2-inline-flex ppv2-items-center ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full poppinsMedium"
                            :class="
                                isOverCapacity(exp)
                                    ? 'ppv2-bg-amber-100 ppv2-text-amber-700'
                                    : 'ppv2-bg-green-100 ppv2-text-green-700'
                            "
                        >
                            {{
                                isOverCapacity(exp)
                                    ? $t('quotes.experience_dates.capacity_over', {
                                          available: exp.nb_available,
                                          max: exp.max_capacity,
                                      })
                                    : $t('quotes.experience_dates.capacity_ok', {
                                          available: exp.nb_available,
                                          max: exp.max_capacity,
                                      })
                            }}
                        </span>
                        <span v-else-if="exp.nb_contributor" class="ppv2-text-black ppv2-text-opacity-50 poppinsRegular">
                            {{ $t('quotes.experience_dates.participants', { count: exp.nb_contributor }) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Bandeau d'alerte (uniquement si au moins une date manque) -->
            <div
                v-if="hasMissing"
                id="exp-dates-alert"
                class="ppv2-mt-4 ppv2-rounded-xl ppv2-bg-red-50 ppv2-px-4 ppv2-py-3 ppv2-text-sm poppinsRegular ppv2-text-red-700"
            >
                {{ missingCount > 1 ? $t('quotes.experience_dates.alert_plural') : $t('quotes.experience_dates.alert') }}
            </div>

            <!-- Note « dates dépassées » : informative, NON bloquante (n'empêche pas la conversion). -->
            <div
                v-if="hasPast"
                class="ppv2-mt-4 ppv2-rounded-xl ppv2-bg-amber-50 ppv2-px-4 ppv2-py-3 ppv2-text-sm poppinsRegular ppv2-text-amber-700"
            >
                {{ $t('quotes.experience_dates.alert_past') }}
            </div>

            <!-- Pied -->
            <div class="ppv2-flex ppv2-justify-end ppv2-gap-3 ppv2-mt-6">
                <button
                    type="button"
                    class="ppv2-px-5 ppv2-py-2.5 ppv2-rounded-lg ppv2-text-sm poppinsMedium ppv2-bg-white ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-15 ppv2-text-[#1d1d1b]"
                    @click="$emit('close')"
                >
                    {{ $t('common.cancel') }}
                </button>
                <button
                    type="button"
                    :disabled="hasMissing || submitting"
                    class="ppv2-px-5 ppv2-py-2.5 ppv2-rounded-lg ppv2-text-sm poppinsMedium ppv2-text-white ppv2-transition-all"
                    :class="hasMissing || submitting ? 'ppv2-bg-gray-300 ppv2-cursor-not-allowed' : ''"
                    :style="hasMissing || submitting ? {} : { backgroundColor: '#7d2dbd' }"
                    @click="submitAndConfirm"
                >
                    {{ $t('quotes.experience_dates.confirm') }}
                </button>
            </div>
        </div>

        <!-- Sélecteur de créneau ISOLÉ posé sur la modale : `slot-only` n'affiche QUE la popup
             planning de QuoteItemStepper (pas la coquille étapes article/participants), ouverte
             directement sur la ligne à dater. Aucune navigation : on date la ligne sur place, la
             modale reste en fond. Ces expériences sont à créneau par définition du garde →
             `uses-time-slots` vaut toujours true. -->
        <QuoteItemStepper
            v-if="editingReservationItem"
            :visible.sync="showSlotEditor"
            :uses-time-slots="true"
            :slot-only="true"
            :editing-item="editingReservationItem"
            @update="handleSlotChosen"
        />
    </div>
</template>

<script>
import { X } from 'lucide-vue'
import QuoteItemStepper from './V3/QuoteItemStepper.vue'

/**
 * Modale « dates d'expérience manquantes » avant validation/facturation d'un devis.
 *
 * Le parent fournit la liste des expériences non datées reçue du serveur (garde-fou
 * QuoteExperienceDateGuard, qui renvoie de quoi rouvrir chaque ligne) et l'`order-id` du devis.
 * « Choisir une date » ouvre SUR PLACE le vrai sélecteur de créneau (QuoteItemStepper), par-dessus
 * la modale — jamais un simple champ date, pour ne pas créer une réservation incomplète, et sans
 * navigation ni changement d'URL. Une fois toutes les lignes datées, le bouton primaire relance
 * l'action bloquée côté parent (`confirm`).
 */
export default {
    name: 'ExperienceDatesModal',
    components: { X, QuoteItemStepper },
    props: {
        show: { type: Boolean, default: false },
        experiences: { type: Array, default: () => [] },
        subtitle: { type: String, default: '' },
        // Order (devis) auquel appartiennent les expériences : requis pour dater une ligne.
        orderId: { type: Number, default: null },
    },
    data() {
        return {
            // Sélecteur de créneau superposé (QuoteItemStepper en mode édition).
            showSlotEditor: false,
            editingReservationItem: null,
            // Créneaux choisis EN MÉMOIRE (order_item_id → payload réservation). Rien n'est persisté
            // tant qu'on ne clique pas « Définir les dates » → « Annuler » ne réserve rien.
            pendingReservations: {},
            // Envoi batch en cours (désactive le bouton primaire).
            submitting: false,
        }
    },
    computed: {
        missingCount() {
            return this.experiences.filter((e) => !e.date).length
        },
        hasMissing() {
            return this.missingCount > 0
        },
        // Au moins une expérience datée dans le passé → note informative (non bloquante).
        hasPast() {
            return this.experiences.some((e) => e.date && e.is_past)
        },
    },
    watch: {
        show(val) {
            if (val) {
                // Session fraîche : on repart sans aucun créneau en attente (une session annulée
                // précédemment ne doit rien laisser).
                this.pendingReservations = {}
                this.submitting = false
                this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus())
            }
        },
    },
    methods: {
        /**
         * La capacité du créneau est-elle connue pour cette expérience ? (calculée côté serveur
         * uniquement pour les lignes datées ; null/absente sinon → on ne l'affiche pas).
         */
        hasCapacity(exp) {
            return (
                exp.nb_available !== null &&
                exp.nb_available !== undefined &&
                exp.max_capacity !== null &&
                exp.max_capacity !== undefined
            )
        },
        /**
         * Warning NON bloquant : le nombre de participants demandés dépasse les places restantes
         * du créneau. Purement informatif — la conversion n'est bloquée que par une date manquante.
         */
        isOverCapacity(exp) {
            return this.hasCapacity(exp) && Number(exp.nb_contributor || 0) > Number(exp.nb_available)
        },
        /**
         * Ouvre le sélecteur de créneau sur la ligne `exp`. On reconstruit l'`editingItem` attendu
         * par le stepper à partir des champs enrichis par le garde (mêmes clés que
         * Create.vue::openSlotEditor) ; le stepper va chercher lui-même créneaux/détails/prix à
         * partir de `item_id`.
         */
        openSlotEditor(exp) {
            this.editingReservationItem = {
                id: exp.id,
                item_id: exp.item_id,
                slug: exp.slug || null,
                price: exp.price,
                quantity: exp.quantity,
                nb_contributor: exp.nb_contributor,
                start_time: exp.start_time,
                end_time: exp.end_time,
                space_id: exp.space_id ?? null,
                duration_id: exp.duration_id ?? null,
                authorize_mixage: exp.authorize_mixage ?? false,
                ticket_selections: exp.ticket_selections || {},
            }
            this.$nextTick(() => {
                this.showSlotEditor = true
            })
        },
        /**
         * Le stepper a validé un créneau : on NE PERSISTE PAS ici. On mémorise la réservation et on
         * marque la ligne « datée » visuellement (pastille « Obligatoire » masquée, bouton primaire
         * activable). La persistance réelle n'a lieu qu'au clic « Définir les dates » (submitAndConfirm)
         * → « Annuler » ne réserve donc aucun créneau.
         */
        handleSlotChosen({ order_item_id, item }) {
            this.$set(this.pendingReservations, order_item_id, {
                prestationSlug: item.prestationSlug,
                space: item.space,
                reservationType: item.reservationType,
                authorize_mixage: item.authorize_mixage,
            })

            const exp = this.experiences.find((e) => Number(e.id) === Number(order_item_id))
            if (exp) {
                // Date affichable (le stepper renvoie le créneau retenu) ; `date` truthy → pastille masquée.
                this.$set(exp, 'date', item.reservationType?.startTime || item.slot?.date || '—')
            }

            // On referme le sélecteur et on revient à la liste (le stepper émet update:visible=false).
            this.showSlotEditor = false
            this.editingReservationItem = null
        },
        /**
         * Bouton « Définir les dates » : on PERSISTE d'abord tous les créneaux choisis (batch,
         * séquentiel), PUIS on relance l'action bloquée côté parent (`confirm`). Si une persistance
         * échoue (créneau devenu indispo, etc.), on n'émet PAS `confirm` — rien ne part en conversion.
         */
        async submitAndConfirm() {
            if (this.submitting) return
            const entries = Object.entries(this.pendingReservations)

            // Aucun créneau à poser (tout était déjà daté côté serveur) → on relance directement.
            if (entries.length === 0) {
                this.$emit('confirm')
                return
            }

            this.submitting = true
            try {
                for (const [orderItemId, reservation] of entries) {
                    const response = await axios.post('/back-office/v3/devis/update-item-reservation', {
                        order_id: this.orderId,
                        order_item_id: Number(orderItemId),
                        reservation,
                    })
                    if (!response.data || !response.data.success) {
                        throw new Error('reservation_failed')
                    }
                    // Retiré dès le succès : un échec plus loin + retry ne re-persistera PAS celui-ci.
                    this.$delete(this.pendingReservations, orderItemId)
                }
                this.$emit('confirm')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.experience_dates.slot_error'))
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>
